import { useI18N } from "@sundae/react-hooks";
import { Text, toolkitCx, Tooltip } from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { FC, useMemo } from "react";

import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import { CARDANO_EXPLORER_LINK_MAP, DEFAULT_DECIMALS } from "../../../constants/cardano.constants";
import { joyrideSelectors } from "../../../constants/joyride.constants";
import { Asset } from "../../../gql/generated/taste-test.sdk";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../../stores";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetName } from "../../../utils/assets.utils";
import { numberI18n } from "../../../utils/number-format";
import BalanceWithAssetName from "../../BalanceWithAssetName";
import CardanoExplorer from "../../CardanoExplorer";
import TasteTestBadges from "../TasteTestBadges/TasteTestBadges";
import TasteTestKPIsMarquee from "../TasteTestKPIsMarquee";
import TasteTestSocials from "../TasteTestSocials";
import TasteTestStatusAndPrice from "../TasteTestStatusAndPrice";
const {
  theme: { colors },
} = require("@sundae/tailwind-config");

const defaultPrimaryColor = colors.primary["DEFAULT"];
const TasteTestDetailBanner: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const {
    state: { tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();
  const {
    state: { cardanoExplorer },
  } = useProfileAppContext();
  const totalSupply = tasteTest?.asset?.totalSupply;
  const tokensSupplied = tasteTest?.currentBalance?.tokenSupplied?.amount;
  const tokensSuppliedPercentageFromTotalSupply = useMemo(() => {
    if (!tokensSupplied || !totalSupply) {
      return 0;
    }

    return (Number(tokensSupplied) / Number(totalSupply)) * 100;
  }, [tokensSupplied, totalSupply]);

  const memoizedTotalSupplyAssetAmount = useMemo(
    () => new AssetAmount(totalSupply, tasteTest?.asset),
    [totalSupply, tasteTest?.asset]
  );
  const memoizedTokenSuppliedAmount = useMemo(
    () => new AssetAmount<Asset>(tokensSupplied, tasteTest?.asset ?? ({} as Asset)),
    [tokensSupplied, tasteTest?.asset]
  );

  if (tasteTest) {
    return (
      <div
        className={cx(
          "relative z-10 border-b border-b-gray-300 pb-8 dark:border-b-gray-600",
          toolkitCx.layout.spacing.top.xl
        )}
        data-guide-selector={joyrideSelectors.guides.tasteTest.interactiveGuide.detailBanner}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            <div className="grid w-full gap-6 lg:flex lg:flex-1">
              {/* -------------------------------------------------------------------------------------------------
               * TT Name + Description
               * -----------------------------------------------------------------------------------------------*/}
              <div className="grid h-fit w-full gap-4">
                <div className="flex items-center gap-2">
                  <Text tag="h2" size="2xl" weight="medium">
                    {tasteTest.asset.assetName} (
                    {
                      getAssetName(
                        tasteTest.asset as unknown as IAssetMetaData
                      ) /*renders the ticker first */
                    }
                    )
                  </Text>

                  {/* -------------------------------------------------------------------------------------------------
                   * Badges for TT
                   * -----------------------------------------------------------------------------------------------*/}
                  <TasteTestBadges />
                </div>

                {/* -------------------------------------------------------------------------------------------------
                 * Socials
                 * -----------------------------------------------------------------------------------------------*/}
                <TasteTestSocials
                  className="h-fit flex-wrap"
                  socials={tasteTest?.projectDetails.socials ?? []}
                />

                {/* -------------------------------------------------------------------------------------------------
                 * Description
                 * -----------------------------------------------------------------------------------------------*/}
                {tasteTest.projectDetails.description && (
                  <Text tag="p" size="sm" variant="muted">
                    {tasteTest.projectDetails.description}
                  </Text>
                )}
                <div className="grid gap-1">
                  {/* -------------------------------------------------------------------------------------------------
                   * Policy ID
                   * -----------------------------------------------------------------------------------------------*/}
                  <Text tag="p" size="sm" variant="muted">
                    {t("policyId", { ns: "glossary" })}:{" "}
                    <CardanoExplorer
                      type={CARDANO_EXPLORER_LINK_MAP.tokenPolicy[cardanoExplorer]}
                      hash={tasteTest.asset.policyId}
                      size="sm"
                      variant="current"
                      style={{
                        color: tasteTestPersonalization?.primaryColor ?? defaultPrimaryColor,
                      }}
                    />
                  </Text>

                  {/* -------------------------------------------------------------------------------------------------
                   * Supplied Amount
                   * -----------------------------------------------------------------------------------------------*/}
                  <div
                    data-guide-selector={
                      joyrideSelectors.guides.tasteTest.interactiveGuide.contributedAmount
                    }
                  >
                    <Text tag="div" size="sm" variant="muted">
                      {
                        getAssetName(
                          tasteTest.asset as unknown as IAssetMetaData
                        ) /*renders the ticker first */
                      }{" "}
                      {t("detail.kpis.tokenSupplied")}:{" "}
                      <div className="inline-flex items-center">
                        <BalanceWithAssetName
                          abbreviate={false}
                          asset={
                            (tasteTest?.asset as unknown as IAssetMetaData) ??
                            ({} as IAssetMetaData)
                          }
                          locale={currentLocale}
                          privacy={false}
                          showSymbol={SundaeUtils.isAdaAsset(tasteTest?.asset)}
                          size="sm"
                          weight="medium"
                          value={memoizedTokenSuppliedAmount}
                        />
                        <Tooltip
                          trigger={<InformationCircleIcon className="ml-1 h-4 w-4 cursor-help" />}
                          tooltipLabel={`${tokensSuppliedPercentageFromTotalSupply.toFixed(
                            2
                          )}% of total supply (${numberI18n(
                            memoizedTotalSupplyAssetAmount?.value.toNumber() ?? 0,
                            {
                              maximumFractionDigits: tasteTest?.asset?.decimals ?? DEFAULT_DECIMALS,
                            },
                            currentLocale
                          )} ${getAssetName(tasteTest?.asset as unknown as IAssetMetaData)})`}
                        />
                      </div>
                    </Text>
                  </div>
                </div>

                {/* -------------------------------------------------------------------------------------------------
                 * KPIs
                 * -----------------------------------------------------------------------------------------------*/}
                <TasteTestKPIsMarquee
                  className="max-w-xl"
                  isStickyMarquee={false}
                  trackInView={false}
                  size="sm"
                  variant="muted"
                />
              </div>
            </div>

            {/* -------------------------------------------------------------------------------------------------
             * Status + Price
             * -----------------------------------------------------------------------------------------------*/}
            <TasteTestStatusAndPrice />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TasteTestDetailBanner;
